import { useOutletContext } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { observable } from 'mobx'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { PortalWrapper } from 'modules/core/components/PortalWrapper'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { employeeLegalEntity } from 'modules/core/lib/employeeLegalEntity'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'
import usersCollection, { employeeUser } from 'modules/core/store/collections/users'

import { companyInFreeTrial } from 'modules/billing/store/collections/subscriptions'
import { ContractCuesProvider } from 'modules/contracts/lib/contractCuesContext'
import ContractCuesBanner from 'modules/contracts/pages/ContractVersionsPage/Id/ContractCuesBanner'
import { type ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import CustomField from 'modules/custom_resources/store/models/customFields/Field'
import Actions from 'modules/employees/components/Actions'
import { ValidatorBlock } from 'modules/employees/components/Personal/_payroll/ValidatorBlock'
import { legalEntityCustomFieldFilter } from 'modules/employees/components/Personal/legalEntityCustomFieldFilter'
import TrialEmployeeTag from 'modules/employees/components/TrialEmployeeTag'
import useEmployeeIncidenceRules from 'modules/employees/hooks/useEmployeeIncidenceRules'
import type { CommonRouteContext } from 'modules/employees/lib/types'
import employeesCollection from 'modules/employees/store/collections/employees'
import Page from 'modules/layout/components/Page'
import { CompanyInstallations } from 'modules/marketplace/store/collections/companyInstallations'
import taxResidences from 'modules/payroll_bundle/store/collections/taxResidences'

import BankInformation from './BankInformation'
import BasicInformation from './BasicInformation'
import ContactInformation from './ContactInformation'
import GeneralInfoBanner from './GeneralInfoBanner'
import PayrollInformation from './PayrollInformation'
import ResidenceInformation from './ResidenceInformation'

const NewPersonal = ({ contractVersions }: { contractVersions: ContractVersions }) => {
  const context = useOutletContext<CommonRouteContext>()
  const { employee, cardEmployeeRight, canEditPersonalInfo } = context
  const companyInstallations = useLazyRef(() => new CompanyInstallations()).current
  const employeeId = employee.attributes.id

  const canEdit = canEditPersonalInfo

  const currentCompany = companies.current
  const inFreeTrial = companyInFreeTrial(currentCompany)

  const { professionalsActivated, hiringIncidence, incidenceRules } =
    useEmployeeIncidenceRules(employeeId)
  const legalEntity = employeeLegalEntity(employee)

  const { loading } = useAsyncData(
    () =>
      Promise.all([
        usersCollection.fetch(),
        employeesCollection.fetch(),
        taxResidences.fetch({
          data: { employee_id: employee.id },
        }),
        companyInstallations.fetch(),
      ]),
    []
  )

  const { isPayrollCustomFieldsBlockVisible, isIrpfVisible, hasintegrations } = ValidatorBlock({
    usageGroups: [CustomField.UsageGroups.generalInformation],
    fieldableModelRef: 'company',
    fieldableId: currentCompany.id,
    valuableModelRef: 'employee',
    valuableInstance: employee,
    employee: employee,
    installedIntegrations: companyInstallations,
    resourceFieldFilter: legalEntityCustomFieldFilter(legalEntity),
  })

  const hasCues =
    (canEdit && professionalsActivated && hiringIncidence && incidenceRules?.length > 0) || false

  const listOfFields = useLazyRef(() => observable.map(new Map())).current

  const hidePayrollInfo = !isPayrollCustomFieldsBlockVisible && !isIrpfVisible && !hasintegrations

  return (
    <Page.Body size="narrow">
      <PortalWrapper container={cardEmployeeRight}>
        {inFreeTrial && <TrialEmployeeTag employee={employee} />}
        <Actions employee={employee} contractVersions={contractVersions} />
      </PortalWrapper>

      <ContractCuesProvider
        collection={{
          hasCues: hasCues,
          incidenceRules: incidenceRules,
          listOfFields: listOfFields,
        }}
      >
        {loading && <Loading />}

        {hasCues && (
          <ContractCuesBanner
            title={i18n.t('employee_form.cues_banner.title', {
              employeeName: employeeUser(employee).firstName,
            })}
            employeeName={employeeUser(employee).firstName}
          ></ContractCuesBanner>
        )}

        <Box gap="s40">
          <GeneralInfoBanner employee={employee} contractVersions={contractVersions} />
          <BasicInformation employee={employee} canEdit={canEdit} />
          <ResidenceInformation employee={employee} canEdit={canEdit} />
          <ContactInformation employee={employee} canEdit={canEdit} />
          {hasFeature('display_new_payroll') && !hidePayrollInfo && (
            <PayrollInformation employee={employee} canEdit={canEdit} />
          )}
          {(!hasFeature('display_new_payroll') || hidePayrollInfo) && (
            <BankInformation employee={employee} canEdit={canEdit} />
          )}
        </Box>
      </ContractCuesProvider>
    </Page.Body>
  )
}

export default observer(NewPersonal)
